import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import {Alert, FlexColumn, Modal, Text, TextArea, TextField} from '@forecast-it/design-system';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../forecast-app/shared/components/modals/types';
import {useForecastFetchQuery} from '../../../forecast-app/shared/hooks/useForecastFetchQuery';
import {BASELINE_TEMPLATE_NAME_TAKEN} from '../../../forecast-app/shared/util/util';
import {CommitMutation} from '../../../mutations/CommitMutation';
import {
	DESCRIPTION_MAX_LENGTH,
	NAME_MAX_LENGTH,
	nameError,
} from '../../../forecast-app/admin-tab/templates-page/baseline/form/baselineTemplateDraft';
import SaveBaselineAsTemplateMutation from './SaveBaselineAsTemplateMutation';
import type {SaveBaselineAsTemplateModalQuery} from './__generated__/SaveBaselineAsTemplateModalQuery.graphql';

const query = graphql`
	query SaveBaselineAsTemplateModalQuery {
		viewer {
			company {
				baselineTemplates {
					name
				}
			}
		}
	}
`;

// The project lost its start date while the modal was open. Shown here: the shared copy talks about inserting.
const PROJECT_DATES_MISSING = 'BASELINE_TEMPLATE_PROJECT_DATES_MISSING';

// The copy holds a phase, time estimate or expense a template cannot (out of range, over the maximum hours, ...).
// Reloading never helps, so the shared "changed while you were editing" copy would mislead.
const COPY_INVALID_CODES = [
	'BASELINE_TEMPLATE_PHASE_INVALID',
	'BASELINE_TEMPLATE_PHASE_REQUIRED',
	'BASELINE_TEMPLATE_TIME_ESTIMATE_INVALID',
	'BASELINE_TEMPLATE_EXPENSE_ESTIMATE_INVALID',
];

export type SaveBaselineAsTemplateModalPayload = {
	/** The project's global id. */
	projectId: string;
	/** "{project} — from {client}", or the project name. */
	defaultName: string;
	/** Gets the saved template's name, once it is saved. */
	onSaved: (name: string) => void;
};

export const showSaveBaselineAsTemplateModal = (props: SaveBaselineAsTemplateModalPayload): void => {
	showModal({type: MODAL_TYPE.SAVE_BASELINE_AS_TEMPLATE, ...props});
};

/** Names and describes a template, then saves the project's Baseline into the template library. */
export const SaveBaselineAsTemplateModal = ({
	projectId,
	defaultName,
	onSaved,
	closeModal,
}: ModalProps<SaveBaselineAsTemplateModalPayload>): JSX.Element => {
	const {formatMessage} = useIntl();
	// A supplementary read for the name check; the page's own data stays on its ForecastQueryRenderer.
	const {data, fetch} = useForecastFetchQuery<SaveBaselineAsTemplateModalQuery['response'], Record<string, never>>(query);
	useEffect(() => {
		fetch({});
	}, []);
	// Until the names arrive, only the server checks for a taken name.
	const existingNames = useMemo(
		() => (data?.viewer?.company?.baselineTemplates ?? []).flatMap(template => (template?.name ? [template.name] : [])),
		[data]
	);

	// Names the server has refused. The fetched list is a snapshot from when the modal opened, so a name someone
	// else saved since is only known once the server rejects it; it stays flagged for the rest of the modal's life.
	const [serverTakenNames, setServerTakenNames] = useState<string[]>([]);
	const takenNames = useMemo(() => [...existingNames, ...serverTakenNames], [existingNames, serverTakenNames]);

	const [name, setName] = useState(defaultName);
	const [description, setDescription] = useState('');
	// A long project and client name can make the prefill too long to save. It is kept whole for the user to
	// shorten, and flagged from the start rather than only once they try to save.
	const [nameErrorId, setNameErrorId] = useState<string | undefined>(() =>
		defaultName.trim().length > NAME_MAX_LENGTH ? 'baseline_templates.errors.name_too_long' : undefined
	);
	const [datesMissing, setDatesMissing] = useState(false);
	const [copyInvalid, setCopyInvalid] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	// Set the moment a save starts. `submitting` only updates on the next render, so a second press in the same
	// tick would otherwise send the save twice.
	const inFlight = useRef(false);
	// Set when the modal is closed while its request is in flight. The template is still saved; its outcome must
	// then neither close a second time, which would pop whatever modal is underneath, nor update this one.
	const closedWhileSubmitting = useRef(false);

	const close = () => {
		if (submitting || inFlight.current) {
			closedWhileSubmitting.current = true;
		}
		closeModal();
	};

	const save = () => {
		if (submitting || inFlight.current) {
			return;
		}
		const problem = nameError(name, takenNames);
		setNameErrorId(problem);
		if (problem) {
			return;
		}
		const templateName = name.trim();
		inFlight.current = true;
		setSubmitting(true);
		setDatesMissing(false);
		setCopyInvalid(false);
		CommitMutation(
			SaveBaselineAsTemplateMutation,
			{projectId, name: templateName, description: description.trim() === '' ? null : description.trim()},
			() => {
				if (!closedWhileSubmitting.current) {
					closeModal();
				}
				onSaved(templateName);
			},
			// No automatic retry: a resend after a lost response would hit the unique name index and report a name
			// the first request had already saved.
			false,
			null,
			(error, onFailure) => {
				inFlight.current = false;
				const nameTaken = String(error).includes(`${BASELINE_TEMPLATE_NAME_TAKEN}:`);
				const datesGone = String(error).includes(`${PROJECT_DATES_MISSING}:`);
				const copyRejected = COPY_INVALID_CODES.some(code => String(error).includes(`${code}:`));
				if (closedWhileSubmitting.current) {
					// Nothing left to flag the problem on, so say it in a modal worded for saving.
					const messageId = nameTaken
						? 'baseline_templates.errors.name_taken'
						: datesGone
						? 'baseline_templates.save.errors.project_dates_missing'
						: copyRejected
						? 'baseline_templates.save.errors.copy_invalid'
						: undefined;
					if (messageId) {
						showModal({
							type: MODAL_TYPE.WARNING,
							warningMessageId: 'baseline_templates.save.title',
							warningInformationIds: [messageId],
						});
					} else {
						onFailure?.(error);
					}
					return;
				}
				setSubmitting(false);
				if (nameTaken) {
					setServerTakenNames(names => [...names, templateName]);
					setNameErrorId('baseline_templates.errors.name_taken');
					return;
				}
				if (datesGone) {
					setDatesMissing(true);
					return;
				}
				if (copyRejected) {
					setCopyInvalid(true);
					return;
				}
				// The shared error modals open beneath this design-system modal, so it closes first or the error could
				// not be seen. Nothing was saved, so the admin can simply open it again.
				closeModal();
				onFailure?.(error);
			}
		);
	};

	const nameErrorText = nameErrorId ? formatMessage({id: nameErrorId}) : undefined;
	return (
		<Modal size={Modal.SIZE.MEDIUM} onCloseModal={close} closeOnInteractOutside={false}>
			<Modal.Title>{formatMessage({id: 'baseline_templates.save.title'})}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap="l">
					<Text color="auxiliary">{formatMessage({id: 'baseline_templates.save.intro'})}</Text>
					{datesMissing ? (
						// The design system's Alert has no live region of its own, so the wrapper announces it.
						<div role="alert" data-cy="save-baseline-as-template-dates-missing">
							<Alert
								type={Alert.TYPE.ERROR}
								text={formatMessage({id: 'baseline_templates.save.errors.project_dates_missing'})}
							/>
						</div>
					) : null}
					{copyInvalid ? (
						<div role="alert" data-cy="save-baseline-as-template-copy-invalid">
							<Alert
								type={Alert.TYPE.ERROR}
								text={formatMessage({id: 'baseline_templates.save.errors.copy_invalid'})}
							/>
						</div>
					) : null}
					<TextField
						label={formatMessage({id: 'baseline_templates.details.name'})}
						data-cy="save-baseline-as-template-name"
						value={name}
						onValueChange={(value: string) => {
							setName(value);
							setNameErrorId(undefined);
						}}
						onBlur={() => setNameErrorId(nameError(name, takenNames))}
						hasError={!!nameErrorText}
						errorText={nameErrorText}
					/>
					{/* TextArea hands onChange the value, not an event, and needs an onBlur. */}
					<TextArea
						label={formatMessage({id: 'baseline_templates.details.description'})}
						data-cy="save-baseline-as-template-description"
						placeholder={formatMessage({id: 'baseline_templates.details.description_placeholder'})}
						isOptional
						height={72}
						maxLength={DESCRIPTION_MAX_LENGTH}
						value={description}
						onChange={(value: string) => setDescription(value)}
						onBlur={() => undefined}
					/>
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={save} disabled={submitting} dataCy="save-baseline-as-template-save">
					{formatMessage({id: 'baseline_templates.save.submit'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={close} disabled={submitting} dataCy="save-baseline-as-template-cancel">
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};
